import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material';
import type { IPublisher } from 'context/pages-context';
import type { CommonPageForm } from 'modules/pages/pages/page-builder-v2/page-builder-v2.types';
import { FC, useEffect, useMemo, useState } from 'react';
import ReactCountryFlag from 'react-country-flag';
import { useController, useFormContext } from 'react-hook-form';
import { publishersApiSlice } from 'store/api/publishers-api/publishers-api';

import { styles } from './publisher-select.styles';

const DEFAULT_PUBLISHER: IPublisher = {
  country: 'us',
  display_name: 'ProductPicks',
  domain: 'productpicks.ai',
  id: 0,
  name: 'ProductPicks',
  reverse_proxy_path: '',
};

export const PublisherSelect: FC<{ disabled?: boolean }> = ({ disabled }) => {
  const { control } = useFormContext<CommonPageForm>();
  const [inputValue, setInputValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const { data, isFetching } = publishersApiSlice.useGetPublishersQuery(debouncedSearch);
  const publishers: IPublisher[] = data?.publishers || [];

  const {
    field,
    fieldState: { error },
  } = useController({ control, name: 'publisher' });

  const options = useMemo(() => {
    return [DEFAULT_PUBLISHER, ...publishers];
  }, [publishers]);

  const handleChange = (newValue: IPublisher | null) => {
    field.onChange(newValue);
  };

  const selectedPublisher = useMemo(() => {
    if (!field.value || field.value.id === undefined) {
      return null;
    }
    return options.find((p) => p.id === field.value?.id) || field.value;
  }, [field.value, options, inputValue]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(inputValue);
    }, 300);
    return () => clearTimeout(timer);
  }, [inputValue]);

  return (
    <Autocomplete
      disableClearable={false}
      disabled={disabled}
      getOptionLabel={(option) => option.display_name || option.name || ''}
      inputValue={inputValue}
      loading={isFetching}
      onChange={(_, newValue) => handleChange(newValue)}
      onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
      options={options}
      renderInput={(params) => (
        <TextField
          {...params}
          error={!!error}
          helperText={error?.message}
          label="Publisher"
          placeholder={!selectedPublisher ? 'Select Publisher' : ''}
          size="small"
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {isFetching ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      renderOption={(props, option) => {
        const countryCode =
          option.country?.toLowerCase() === 'uk' ? 'GB' : option.country?.toUpperCase() || 'US';

        return (
          <Box component="li" {...props} sx={styles.publisherOption}>
            <ReactCountryFlag countryCode={countryCode} style={{ fontSize: '1.2em' }} svg />
            <Typography>{option.display_name || option.name}</Typography>
          </Box>
        );
      }}
      value={selectedPublisher}
    />
  );
};
